import { useState, useEffect, useRef } from "react";
import { supabase } from "./db";
import * as XLSX from "xlsx";

// 👇 FIXNÉ STĹPCE PRE SKU DASHBOARD
const FIXED_COLUMNS = [
  "sku_status",
  "sku_assigned_at",
  "company_name",
  "internal_sku",
];

// Pekné mapovanie systémových názvov stĺpcov na slovenské popisky
const COLUMN_NAMES_MAP = {
  ean: "EAN Kód",
  product_name: "Názov produktu",
  category: "Kategória na webe",
  price: "Cena výrobcu (€)",
  stock: "Sklad (ks)",
  brand: "Brand",
  created_at: "Dátum pridania",
  approved_at: "Čas rozhodnutia",
  weight_brutto_g: "Váha Brutto (g)",
  weight_netto_g: "Váha Netto (g)",
  short_description: "Krátky text",
  description: "Hlavný popis",
  image_url: "Foto",
  product_line: "Produktová rada",
  sukl: "ŠÚKL",
  adc: "ADC kód",
  predecessor: "Predchodca",
  dr_max_code: "Kód Dr.Max",
  base_mj: "Základná MJ",
  ratio_to_zmj: "Pomer k ZMJ",
  packaging: "Balenie",
  ingredients: "Zloženie",
  dosage: "Dávkovanie",
  nutritional_values: "Výživové hodnoty",
  width_mm: "Šírka (mm)",
  height_mm: "Výška (mm)",
  depth_mm: "Hĺbka (mm)",
  pieces_in_carton: "Ks v kartóne",
  pieces_on_pallet: "Ks na palete",
  manufacturer: "Výrobca",
  country_of_origin: "Krajina pôvodu",
  customs_code: "Colný sadzobník",
  recycling_fee: "Recyklačný poplatok",
  vat_percent: "% DPH",
  dmoc: "DMOC (€)",
  returnable_packaging: "Zálohovaný obal",
  total_weight: "Celková hmotnosť",
  listing_date: "Zalistovanie od",
  listing_type: "Typ listingu",
  admin_fee: "Admin poplatok",
  turnover_reward_percent: "Odplata z obratu (%)",
  tracked_transaction: "Sledovaná transakcia",
  tracked_price: "Sledovaná cena",
};

export default function SkuDashboard({ session }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draftSkus, setDraftSkus] = useState({});

  // Stavy pre dynamické stĺpce
  const [dbColumns, setDbColumns] = useState([]);
  const [selectedColumns, setSelectedColumns] = useState([
    "product_name",
    "ean",
    "category",
    "price",
    "stock",
  ]);
  const [showColumnChooser, setShowColumnChooser] = useState(false);
  const columnChooserRef = useRef(null);

  // Filtre a triedenie
  const [colFilters, setColFilters] = useState({});
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  // Stránkovanie
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);

  useEffect(() => {
    fetchColumnsFromDB();
    fetchApprovedProducts();
  }, []);

  // Zatváranie menu pri kliknutí vedľa
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        columnChooserRef.current &&
        !columnChooserRef.current.contains(event.target)
      ) {
        setShowColumnChooser(false);
      }
    };
    if (showColumnChooser)
      document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showColumnChooser]);

  const fetchColumnsFromDB = async () => {
    const { data, error } = await supabase.rpc("get_products_columns");
    if (!error && data && data.length > 0) {
      const exclude = [
        "id",
        "supplier_id",
        "locked_by_master",
        "approved_at",
        "status",
        "content_status",
        "internal_sku",
        "updated_at",
        "sku_assigned_at",
      ];
      let cleanCols = data.filter((c) => !exclude.includes(c));
      cleanCols.sort((a, b) =>
        (COLUMN_NAMES_MAP[a] || a).localeCompare(COLUMN_NAMES_MAP[b] || b)
      );
      setDbColumns(cleanCols);
    }
  };

  const fetchApprovedProducts = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("products")
      .select(`*, profiles:supplier_id (company_name)`)
      .eq("status", "Schválené");

    if (!error) {
      const formattedData = (data || []).map((p) => ({
        ...p,
        display_company:
          p.profiles?.company_name ||
          p.company_name ||
          p.user_email ||
          "Neznámy dodávateľ",
      }));
      setProducts(formattedData);
    }
    setLoading(false);
  };

  const handleSkuSave = async (productId, newSku) => {
    const timestamp = new Date().toISOString();
    const { error } = await supabase
      .from("products")
      .update({ internal_sku: String(newSku), sku_assigned_at: timestamp })
      .eq("id", productId);

    if (error) {
      alert("Chyba pri ukladaní SKU.");
    } else {
      setProducts((prev) =>
        prev.map((p) =>
          p.id === productId
            ? { ...p, internal_sku: String(newSku), sku_assigned_at: timestamp }
            : p
        )
      );
      setDraftSkus((prev) => {
        const newState = { ...prev };
        delete newState[productId];
        return newState;
      });
    }
  };

  const updateColFilter = (field, value) => {
    setColFilters((prev) => ({ ...prev, [field]: value }));
  };

  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc")
      direction = "desc";
    setSortConfig({ key, direction });
  };

  const getSortIcon = (columnName) => {
    if (sortConfig.key !== columnName) return " ↕️";
    return sortConfig.direction === "asc" ? " 🔼" : " 🔽";
  };

  // 👇 DYNAMICKÁ FILTRÁCIA
  const filteredProducts = products.filter((p) => {
    const activeFilters = Object.entries(colFilters).filter(
      ([, val]) => val !== ""
    );

    for (const [key, filterVal] of activeFilters) {
      if (key === "sku_status") {
        const hasSku = Boolean(p.internal_sku);
        if (filterVal === "priradene" && !hasSku) return false;
        if (filterVal === "cakajuce" && hasSku) return false;
        continue;
      }

      let cellVal =
        key === "company_name"
          ? p.display_company + " " + (p.user_email || "")
          : p[key];
      if (
        !String(cellVal || "")
          .toLowerCase()
          .includes(filterVal.toLowerCase())
      )
        return false;
    }
    return true;
  });

  // 👇 DYNAMICKÉ ZORAĎOVANIE
  const sortedProducts = [...filteredProducts].sort((a, b) => {
    if (!sortConfig.key) return 0;

    if (sortConfig.key === "sku_status") {
      const hasA = Boolean(a.internal_sku);
      const hasB = Boolean(b.internal_sku);
      if (hasA === hasB) return 0;
      return sortConfig.direction === "asc" ? (hasA ? -1 : 1) : hasA ? 1 : -1;
    }

    let valA =
      sortConfig.key === "company_name" ? a.display_company : a[sortConfig.key];
    let valB =
      sortConfig.key === "company_name" ? b.display_company : b[sortConfig.key];

    if (
      sortConfig.key === "created_at" ||
      sortConfig.key === "sku_assigned_at"
    ) {
      valA = new Date(valA || 0).getTime();
      valB = new Date(valB || 0).getTime();
    } else if (!isNaN(parseFloat(valA)) && !isNaN(parseFloat(valB))) {
      valA = parseFloat(valA) || 0;
      valB = parseFloat(valB) || 0;
    } else {
      valA = String(valA || "").toLowerCase();
      valB = String(valB || "").toLowerCase();
    }

    if (valA < valB) return sortConfig.direction === "asc" ? -1 : 1;
    if (valA > valB) return sortConfig.direction === "asc" ? 1 : -1;
    return 0;
  });

  // Stránkovanie
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems =
    itemsPerPage === "all"
      ? sortedProducts
      : sortedProducts.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages =
    itemsPerPage === "all"
      ? 1
      : Math.ceil(sortedProducts.length / itemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  useEffect(() => {
    setCurrentPage(1);
  }, [colFilters]);

  // Export do Excelu
  const exportToExcel = () => {
    const dataToExport = sortedProducts.map((p) => {
      const row = {};
      row["Stav"] = p.internal_sku ? "✅ Priradené" : "⏳ Čaká sa";
      row["Čas priradenia"] = p.sku_assigned_at
        ? new Date(p.sku_assigned_at).toLocaleString("sk-SK")
        : "-";
      row["Dodávateľ"] = p.display_company;
      row["Interné SKU"] = p.internal_sku || "";

      selectedColumns.forEach((col) => {
        const label = COLUMN_NAMES_MAP[col] || col;
        row[label] =
          col === "created_at"
            ? new Date(p[col]).toLocaleDateString("sk-SK")
            : p[col];
      });
      return row;
    });

    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Ciselnik_Export");
    XLSX.writeFile(wb, "Export_Ciselnik.xlsx");
  };

  return (
    <div className="card" style={{ padding: "20px", marginTop: "20px" }}>
      {/* HLAVIČKA A AKCIE */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "20px",
          flexWrap: "wrap",
          gap: "15px",
        }}
      >
        <div>
          <h2 style={{ color: "#1e293b", margin: 0 }}>🔢 Správa číselníka</h2>
          <p style={{ color: "#64748b", fontSize: "14px" }}>
            Zobrazuje sa {sortedProducts.length} z {products.length} produktov.
          </p>
        </div>

        <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
          <div style={{ position: "relative" }} ref={columnChooserRef}>
            <button
              onClick={() => setShowColumnChooser(!showColumnChooser)}
              className="btn-outline"
              style={{
                display: "flex",
                gap: "8px",
                alignItems: "center",
                backgroundColor: "white",
              }}
            >
              ⚙️ Zobraziť stĺpce (
              {selectedColumns.length + FIXED_COLUMNS.length})
            </button>
            {showColumnChooser && (
              <div style={columnChooserPopupStyle}>
                <div style={popupHeaderStyle}>Vyberte stĺpce pre výpis:</div>
                <div style={popupContentStyle}>
                  {FIXED_COLUMNS.map((col) => (
                    <label key={col} style={popupItemDisabledStyle}>
                      <input
                        type="checkbox"
                        checked
                        disabled
                        style={{
                          margin: 0,
                          cursor: "not-allowed",
                          width: "16px",
                          height: "16px",
                          flexShrink: 0,
                        }}
                      />
                      <span style={{ flex: 1, textAlign: "left" }}>
                        {col === "sku_status"
                          ? "Stav"
                          : col === "sku_assigned_at"
                          ? "Čas priradenia"
                          : col === "company_name"
                          ? "Dodávateľ"
                          : "Interné SKU"}
                        <span
                          style={{
                            color: "#94a3b8",
                            fontSize: "11px",
                            fontStyle: "italic",
                            marginLeft: "5px",
                          }}
                        >
                          (fixný)
                        </span>
                      </span>
                    </label>
                  ))}
                  {dbColumns.map((col) => (
                    <label key={col} style={popupItemStyle}>
                      <input
                        type="checkbox"
                        checked={selectedColumns.includes(col)}
                        onChange={(e) => {
                          if (e.target.checked)
                            setSelectedColumns([...selectedColumns, col]);
                          else
                            setSelectedColumns(
                              selectedColumns.filter((c) => c !== col)
                            );
                        }}
                        style={{
                          margin: 0,
                          cursor: "pointer",
                          width: "16px",
                          height: "16px",
                          flexShrink: 0,
                        }}
                      />
                      <span style={{ flex: 1, textAlign: "left" }}>
                        {COLUMN_NAMES_MAP[col] || col}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>

          <button
            onClick={exportToExcel}
            className="btn-success"
            style={{ display: "flex", alignItems: "center", gap: "8px" }}
          >
            📊 Exportovať Excel
          </button>
        </div>
      </div>

      <div
        style={{
          overflowX: "auto",
          border: "1px solid #e2e8f0",
          borderRadius: "12px",
          minHeight: "500px",
        }}
      >
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize: "13px",
          }}
        >
          <thead>
            <tr
              style={{
                backgroundColor: "#f8fafc",
                textAlign: "left",
                borderBottom: "2px solid #cbd5e1",
              }}
            >
              {/* FIXNÝ: STAV */}
              <th style={{ ...thStyle, minWidth: "140px" }}>
                <div
                  style={sortableHeaderContent}
                  onClick={() => requestSort("sku_status")}
                >
                  Stav {getSortIcon("sku_status")}
                </div>
                <select
                  value={colFilters.sku_status || ""}
                  onChange={(e) =>
                    updateColFilter("sku_status", e.target.value)
                  }
                  style={filterInputStyle}
                >
                  <option value="">Všetko</option>
                  <option value="priradene">✅ Priradené</option>
                  <option value="cakajuce">⏳ Čakajúce</option>
                </select>
              </th>

              {/* FIXNÝ: ČAS PRIRADENIA */}
              <th style={{ ...thStyle, minWidth: "140px" }}>
                <div
                  style={sortableHeaderContent}
                  onClick={() => requestSort("sku_assigned_at")}
                >
                  Čas priradenia {getSortIcon("sku_assigned_at")}
                </div>
              </th>

              {/* FIXNÝ: DODÁVATEĽ */}
              <th style={{ ...thStyle, minWidth: "180px" }}>
                <div
                  style={sortableHeaderContent}
                  onClick={() => requestSort("company_name")}
                >
                  Dodávateľ {getSortIcon("company_name")}
                </div>
                <input
                  type="text"
                  placeholder="Filter..."
                  value={colFilters.company_name || ""}
                  onChange={(e) =>
                    updateColFilter("company_name", e.target.value)
                  }
                  style={filterInputStyle}
                />
              </th>

              {/* FIXNÝ: SKU */}
              <th
                style={{
                  ...thStyle,
                  minWidth: "180px",
                  backgroundColor: "#fffbeb",
                }}
              >
                <div
                  style={sortableHeaderContent}
                  onClick={() => requestSort("internal_sku")}
                >
                  Interné SKU {getSortIcon("internal_sku")}
                </div>
                <input
                  type="text"
                  placeholder="Filter SKU..."
                  value={colFilters.internal_sku || ""}
                  onChange={(e) =>
                    updateColFilter("internal_sku", e.target.value)
                  }
                  style={filterInputStyle}
                />
              </th>

              {/* DYNAMICKÉ STĹPCE */}
              {selectedColumns.map((colKey) => {
                const label = COLUMN_NAMES_MAP[colKey] || colKey;
                const minWidth =
                  colKey === "image_url"
                    ? "60px"
                    : colKey === "short_description" || colKey === "description"
                    ? "300px"
                    : "150px";

                return (
                  <th key={colKey} style={{ ...thStyle, minWidth }}>
                    {colKey === "image_url" ? (
                      "Foto"
                    ) : (
                      <>
                        <div
                          style={sortableHeaderContent}
                          onClick={() => requestSort(colKey)}
                        >
                          {label} {getSortIcon(colKey)}
                        </div>
                        <input
                          type="text"
                          style={filterInputStyle}
                          placeholder={`Filter...`}
                          onChange={(e) =>
                            updateColFilter(colKey, e.target.value)
                          }
                        />
                      </>
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td
                  colSpan={selectedColumns.length + 4}
                  style={{
                    textAlign: "center",
                    padding: "40px",
                    color: "#64748b",
                  }}
                >
                  Načítavam dáta zo systému...
                </td>
              </tr>
            ) : currentItems.length === 0 ? (
              <tr>
                <td
                  colSpan={selectedColumns.length + 4}
                  style={{
                    padding: "40px",
                    textAlign: "center",
                    color: "#64748b",
                  }}
                >
                  Zadaným filtrom nevyhovujú žiadne produkty.
                </td>
              </tr>
            ) : (
              currentItems.map((product) => {
                const currentInputValue =
                  draftSkus[product.id] !== undefined
                    ? draftSkus[product.id]
                    : product.internal_sku || "";
                const isModified =
                  draftSkus[product.id] !== undefined &&
                  draftSkus[product.id] !== (product.internal_sku || "");
                const hasSku = Boolean(product.internal_sku);

                return (
                  <tr
                    key={product.id}
                    style={{ borderBottom: "1px solid #e2e8f0" }}
                  >
                    {/* STAV */}
                    <td style={tdStyle}>
                      {hasSku ? (
                        <span
                          style={{
                            backgroundColor: "#dcfce7",
                            color: "#166534",
                            padding: "4px 8px",
                            borderRadius: "6px",
                            fontSize: "11px",
                            fontWeight: "bold",
                            display: "inline-block",
                          }}
                        >
                          ✅ Priradené
                        </span>
                      ) : (
                        <span
                          style={{
                            backgroundColor: "#fef9c3",
                            color: "#854d0e",
                            padding: "4px 8px",
                            borderRadius: "6px",
                            fontSize: "11px",
                            fontWeight: "bold",
                            display: "inline-block",
                          }}
                        >
                          ⏳ Čaká sa
                        </span>
                      )}
                    </td>

                    {/* ČAS PRIRADENIA */}
                    <td style={tdStyle}>
                      {hasSku && product.sku_assigned_at ? (
                        <div style={{ fontSize: "12px", color: "#64748b" }}>
                          {new Date(product.sku_assigned_at).toLocaleString(
                            "sk-SK",
                            {
                              day: "2-digit",
                              month: "2-digit",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            }
                          )}
                        </div>
                      ) : (
                        <span style={{ color: "#94a3b8" }}>-</span>
                      )}
                    </td>

                    {/* DODÁVATEĽ */}
                    <td style={tdStyle}>
                      <div
                        style={{
                          fontWeight: "bold",
                          color: "#0f172a",
                          marginBottom: "2px",
                        }}
                      >
                        {product.display_company}
                      </div>
                      {product.user_email &&
                        product.user_email !== product.display_company && (
                          <div style={{ color: "#64748b", fontSize: "11px" }}>
                            {product.user_email}
                          </div>
                        )}
                    </td>

                    {/* SKU INPUT */}
                    <td style={{ ...tdStyle, backgroundColor: "#fffbeb" }}>
                      <input
                        type="text"
                        value={currentInputValue}
                        onChange={(e) =>
                          setDraftSkus({
                            ...draftSkus,
                            [product.id]: e.target.value,
                          })
                        }
                        placeholder="Zadaj kód..."
                        style={{
                          padding: "8px",
                          borderRadius: "6px",
                          border: "2px solid #fbbf24",
                          width: "100%",
                          fontWeight: "bold",
                          color: "#b45309",
                          outline: "none",
                          transition: "all 0.2s",
                        }}
                      />
                      {isModified && (
                        <div style={{ marginTop: "8px" }}>
                          <button
                            onClick={() =>
                              handleSkuSave(product.id, currentInputValue)
                            }
                            style={{
                              width: "100%",
                              padding: "6px 10px",
                              backgroundColor: "#22c55e",
                              color: "white",
                              border: "2px solid #166534",
                              borderRadius: "6px",
                              fontWeight: "bold",
                              cursor: "pointer",
                              fontSize: "12px",
                              textTransform: "uppercase",
                              letterSpacing: "0.5px",
                              boxShadow: "0 4px 6px rgba(34, 197, 94, 0.4)",
                            }}
                          >
                            💾 Uložiť
                          </button>
                        </div>
                      )}
                    </td>

                    {/* DYNAMICKÉ HODNOTY */}
                    {selectedColumns.map((colKey) => (
                      <td key={colKey} style={tdStyle}>
                        {colKey === "image_url" ? (
                          product.image_url ? (
                            <img
                              src={product.image_url}
                              style={{
                                width: "40px",
                                borderRadius: "4px",
                                objectFit: "cover",
                              }}
                              alt="produkt"
                            />
                          ) : (
                            "-"
                          )
                        ) : colKey === "created_at" ? (
                          new Date(product.created_at).toLocaleDateString(
                            "sk-SK"
                          )
                        ) : colKey === "price" ? (
                          <strong style={{ color: "#059669" }}>
                            {product[colKey]} €
                          </strong>
                        ) : (
                          <div
                            style={
                              colKey === "short_description" ||
                              colKey === "description"
                                ? multiEllipsisStyle
                                : ellipsisStyle
                            }
                            title={product[colKey]}
                          >
                            {product[colKey] || "-"}
                          </div>
                        )}
                      </td>
                    ))}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>

        {/* 👇 STRÁNKOVANIE 👇 */}
        {!loading && sortedProducts.length > 0 && (
          <div style={paginationContainerStyle}>
            <div style={{ fontSize: "13px", color: "#64748b" }}>
              Zobrazených {indexOfFirstItem + 1} -{" "}
              {Math.min(indexOfLastItem, sortedProducts.length)} z{" "}
              <strong>{sortedProducts.length}</strong> produktov
            </div>
            <div style={{ display: "flex", gap: "5px" }}>
              <button
                onClick={() => paginate(currentPage - 1)}
                disabled={currentPage === 1 || itemsPerPage === "all"}
                style={pageBtnStyle(
                  currentPage === 1 || itemsPerPage === "all"
                )}
              >
                Predchádzajúca
              </button>
              {itemsPerPage !== "all" &&
                Array.from({ length: totalPages }, (_, i) => i + 1).map(
                  (number) => {
                    if (
                      number === 1 ||
                      number === totalPages ||
                      (number >= currentPage - 2 && number <= currentPage + 2)
                    ) {
                      return (
                        <button
                          key={number}
                          onClick={() => paginate(number)}
                          style={
                            currentPage === number
                              ? activePageBtnStyle
                              : pageBtnStyle(false)
                          }
                        >
                          {number}
                        </button>
                      );
                    } else if (
                      number === currentPage - 3 ||
                      number === currentPage + 3
                    )
                      return (
                        <span
                          key={number}
                          style={{ padding: "5px 10px", color: "#94a3b8" }}
                        >
                          ...
                        </span>
                      );
                    return null;
                  }
                )}
              <button
                onClick={() => paginate(currentPage + 1)}
                disabled={currentPage === totalPages || itemsPerPage === "all"}
                style={pageBtnStyle(
                  currentPage === totalPages || itemsPerPage === "all"
                )}
              >
                Ďalšia
              </button>
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                fontSize: "13px",
                color: "#475569",
              }}
            >
              <span>Zobraziť po:</span>
              <select
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(
                    e.target.value === "all" ? "all" : Number(e.target.value)
                  );
                  setCurrentPage(1);
                }}
                style={pageSelectStyle}
              >
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value="all">Všetky</option>
              </select>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// 🔥 CSS-IN-JS STYLY
const thStyle = {
  padding: "12px 10px",
  color: "#334155",
  verticalAlign: "top",
  borderLeft: "1px solid #e2e8f0",
};
const sortableHeaderContent = {
  display: "flex",
  alignItems: "center",
  gap: "5px",
  cursor: "pointer",
  fontWeight: "bold",
  userSelect: "none",
};
const tdStyle = {
  padding: "12px 10px",
  verticalAlign: "top",
  borderLeft: "1px solid #e2e8f0",
};
const filterInputStyle = {
  display: "block",
  width: "100%",
  padding: "6px 8px",
  fontSize: "11px",
  marginTop: "8px",
  borderRadius: "6px",
  border: "1px solid #cbd5e1",
  outline: "none",
  boxSizing: "border-box",
  backgroundColor: "white",
  fontWeight: "normal",
};
const ellipsisStyle = {
  maxWidth: "150px",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  color: "#475569",
};
const multiEllipsisStyle = {
  maxWidth: "300px",
  display: "-webkit-box",
  webkitLineClamp: "3",
  webkitBoxOrient: "vertical",
  overflow: "hidden",
  color: "#475569",
};

// STYLY PRE MENU STĹPCOV
const columnChooserPopupStyle = {
  position: "absolute",
  top: "100%",
  left: "0",
  marginTop: "12px",
  background: "#ffffff",
  border: "1px solid #e2e8f0",
  borderRadius: "16px",
  padding: "20px",
  boxShadow:
    "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)",
  zIndex: 1000,
  width: "300px",
};
const popupHeaderStyle = {
  fontWeight: "800",
  fontSize: "15px",
  color: "#1e293b",
  marginBottom: "15px",
  paddingBottom: "10px",
  borderBottom: "1px solid #e2e8f0",
  textAlign: "left",
};
const popupContentStyle = {
  maxHeight: "350px",
  overflowY: "auto",
  display: "flex",
  flexDirection: "column",
  gap: "4px",
};
const popupItemStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-start",
  gap: "10px",
  padding: "6px 8px",
  fontSize: "13px",
  color: "#334155",
  cursor: "pointer",
  borderRadius: "8px",
  userSelect: "none",
  margin: "0",
  textAlign: "left",
  transition: "background-color 0.2s",
};
const popupItemDisabledStyle = {
  ...popupItemStyle,
  color: "#94a3b8",
  cursor: "not-allowed",
  backgroundColor: "#f8fafc",
};

// STYLY PRE STRÁNKOVANIE
const paginationContainerStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "15px 20px",
  borderTop: "1px solid #e2e8f0",
  backgroundColor: "#f8fafc",
  flexWrap: "wrap",
  gap: "15px",
};
const pageBtnStyle = (disabled) => ({
  padding: "6px 12px",
  border: "1px solid #cbd5e1",
  backgroundColor: disabled ? "#f1f5f9" : "white",
  color: disabled ? "#94a3b8" : "#334155",
  borderRadius: "6px",
  cursor: disabled ? "not-allowed" : "pointer",
  fontSize: "13px",
  fontWeight: "600",
  transition: "all 0.2s",
});
const activePageBtnStyle = {
  ...pageBtnStyle(false),
  backgroundColor: "#0f172a",
  color: "white",
  borderColor: "#0f172a",
};
const pageSelectStyle = {
  padding: "6px 10px",
  borderRadius: "6px",
  border: "1px solid #cbd5e1",
  fontSize: "13px",
  outline: "none",
  cursor: "pointer",
  backgroundColor: "white",
};
